import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useFormikContext} from 'formik';
import {isEqual} from 'lodash';
import excel from 'shared/Excel';
import {Fieldset} from 'components/customer/Product/entity/Fieldset';
import {toNumber} from 'components/customer/Product/Drawer/helpers';
import {
    applyShape,
    Face,
    reshare,
    seedAuto,
    setFaceAuto,
    setFaceHeight,
    shareEqually,
    toFaces,
} from 'components/customer/Product/Drawer/helpers/componentFaces';

const DEFAULT_FACE_FIELD = 'drawer_face_height';
const DEFAULT_GAP_FIELD = 'cabinet_drawer_gap';

interface FaceValues {
    [key: string]: unknown;
}

interface ComponentFaces {
    faces: Face[];
    total: number;
    gap: number;
    fieldName: string;
    setHeight: (index: number, height: number) => void;
    setAuto: (index: number) => void;
    makeAllEqual: () => void;
    applyPreset: (shape: (number | null)[]) => void;
}

// The faces of a standalone drawer face product, which holds them as a flat
// list of heights with nowhere to record which ones the customer chose. That
// is kept here instead and seeded from the heights themselves, so a face set
// on purpose survives an edit to another one.
export const useComponentFaces = (fieldset: Fieldset): ComponentFaces => {
    const {values, setFieldValue} = useFormikContext<FaceValues>();

    const fieldName = fieldset?.options?.faceHeightField ?? DEFAULT_FACE_FIELD;
    const gapFieldName = fieldset?.options?.drawerGap ?? DEFAULT_GAP_FIELD;

    // a saved height comes back as text, and the sharing adds them up, so one
    // left as a string turns the sum into a concatenation
    const heights = useMemo(
        () =>
            ((values[String(fieldName)] as (number | string)[]) ?? []).map(
                toNumber
            ),
        [values, fieldName]
    );

    const total = useMemo(() => {
        try {
            return excel.calculate<number>(
                fieldset?.options?.totalHeight,
                values
            );
        } catch (error) {
            return 0;
        }
    }, [fieldset, values]);

    const quantity = useMemo(() => {
        const declared = fieldset?.quantity ?? 1;

        return isNaN(Number(declared))
            ? toNumber(values[String(declared)])
            : Number(declared);
    }, [fieldset, values]);

    const gap = toNumber(values[String(gapFieldName)]);

    const [autoFaces, setAutoFaces] = useState<boolean[]>(null);
    const previous = useRef<{total: number; quantity: number}>(null);

    useEffect(() => {
        if (!(total > 0) || !(quantity > 0)) return;

        const opening = previous.current === null;
        const countChanged = !opening && previous.current.quantity !== quantity;

        previous.current = {total, quantity};

        // opened as it was saved: work out which faces the customer decided
        if (opening && heights.length === quantity) {
            setAutoFaces(seedAuto(heights, total));

            return;
        }

        const auto =
            autoFaces?.length === heights.length
                ? autoFaces
                : seedAuto(heights, total);

        // a different number of drawers is a fresh layout, where a panel or a
        // gap only changes the space the faces already agreed how to divide
        const next =
            countChanged || heights.length !== quantity
                ? shareEqually(quantity, total)
                : reshare({heights, auto}, total);

        setAutoFaces(next.auto);

        if (!isEqual(next.heights, heights)) {
            void setFieldValue(fieldName, next.heights);
        }
        // the heights are what this writes, so watching them would loop
    }, [total, quantity]);

    const faces = useMemo(() => {
        const auto =
            autoFaces?.length === heights.length
                ? autoFaces
                : seedAuto(heights, total);

        return toFaces(heights, auto);
    }, [heights, autoFaces, total]);

    const commit = useCallback(
        (next: {heights: number[]; auto: boolean[]}) => {
            setAutoFaces(next.auto);
            void setFieldValue(fieldName, next.heights);
        },
        [fieldName, setFieldValue]
    );

    const state = useMemo(
        () => ({heights, auto: faces.map((face) => face.isAuto)}),
        [heights, faces]
    );

    const setHeight = useCallback(
        (index: number, height: number) => {
            commit(setFaceHeight(state, total, index, height));
        },
        [state, total, commit]
    );

    const setAuto = useCallback(
        (index: number) => {
            commit(setFaceAuto(state, total, index));
        },
        [state, total, commit]
    );

    const makeAllEqual = useCallback(() => {
        commit(shareEqually(heights.length, total));
    }, [heights.length, total, commit]);

    const applyPreset = useCallback(
        (shape: (number | null)[]) => {
            commit(applyShape(shape, total));
        },
        [total, commit]
    );

    return {
        faces,
        total,
        gap,
        fieldName,
        setHeight,
        setAuto,
        makeAllEqual,
        applyPreset,
    };
};
